<template>
  <div
    class="container-review d-flex justify-content-end align-items-center"
    v-if="research && !showAuthModal"
  >
    <div class="top-bar p-3 fixed-top-bar d-flex justify-content-end">
      <div class="d-flex justify-content-end align-items-center">
        <v-dropdown-menu
          v-if="!isExternal"
          icon="ellipsis-h"
          style-type="link"
        >
          <template v-slot:menu>
            <button
              class="dropdown-item"
              type="button"
              @click="saveAndLeave()"
            >
              <i class="fa fa-home"></i> {{ $t('phrase.back_to_dashboard') }}
            </button>
          </template>
        </v-dropdown-menu>
      </div>
    </div>
    <div
      class="research-item-container d-flex flex-wrap justify-content-center"
      v-if="researchItem"
    >
      <div class="d-block d-md-none container">
        <!-- Small screens -->
        <div class="progress mb-5">
          <div
            class="progress-bar bg-cr-red-black"
            :style="{width: `${progress}%`}"
          ></div>
        </div>

        <transition :name="transitionName">
          <div class="research-item-inner" v-if="!loading">
            <div class="text-center mb-3">
              <h4
                class="m-0"
                v-html="formattedItemText(researchItem.title_with_answer)"
              />
              <br/>
              <small class="help-text"><em>{{ researchItem.help_text }}</em></small>
            </div>

            <div>
              <component
                :is="ITEM_COMPONENTS[researchItem.render_type]"
                id-prefix="mobile"
                :research-item="researchItem"
                @value-changed="saveAndNextOrFinish"
              />
            </div>
          </div>
        </transition>
      </div>
      <div class="d-none d-md-block container">
        <!-- Medium and large screens -->
        <div class="progress">
          <div
            class="progress-bar bg-cr-red-black"
            :style="{width: `${progress}%`}"
          >
          </div>
        </div>
        <div class="w-100 h-100 card">
          <div
            class="alert alert-warning p-1 d-flex justify-content-center align-items-center"
            v-if="isTestReview"
          >
            <div class="mr-2">
              {{ $t('sentence.test_review') }}
            </div>
            <v-button
              icon="redo-alt"
              text="phrase.restart"
              size="xs"
              @click="restartResearch()"
            />
          </div>
          <div class="card-body">
            <transition :name="transitionName">
              <div v-if="!loading">
                <div class="text-center mb-3">
                  <label
                    class="label mb-0"
                    v-html="formattedItemText(researchItem.title_with_answer)"
                  />
                  <br/>
                  <span class="help-text">
                    <em>{{ researchItem.help_text }}</em>
                  </span>
                </div>

                <div>
                  <component
                    :is="ITEM_COMPONENTS[researchItem.render_type]"
                    :research-item="researchItem"
                    @value-changed="saveAndNextOrFinish"
                  />
                </div>
              </div>
            </transition>
          </div>
          <div class="card-footer d-flex justify-content-between" v-if="researchItem">
            <div>
              <v-button
                icon="chevron-left"
                style-type="secondary"
                v-if="researchItem.previous_research_item"
                @click="saveAndPrevious()"
                :disabled="loading"
                id="review-research-previous"
              />
            </div>
            <div>
              <v-button
                v-if="researchItem.has_next"
                icon="chevron-right"
                @click="saveAndNext()"
                :disabled="loading || !answerIsValid"
                id="review-research-next"
              />
              <v-button
                v-else
                text="word.finish"
                @click="saveAndFinish()"
                id="review-research-finish"
                :disabled="loading || !answerIsValid"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="d-block d-md-none">
      <!-- Small screens -->
      <div class="btn-left p-3" v-if="researchItem">
        <v-button
          icon="chevron-left"
          style-type="secondary"
          v-if="researchItem.previous_research_item"
          @click="saveAndPrevious()"
          :disabled="loading"
          id="mobile-review-research-previous"
        />
      </div>
      <div class="btn-right p-3" v-if="researchItem">
        <v-button
          v-if="researchItem.has_next"
          icon="chevron-right"
          @click="saveAndNext()"
          id="mobile-review-research-next"
          :disabled="loading || !answerIsValid"
        />
        <v-button
          v-else
          text="word.finish"
          @click="saveAndFinish()"
          id="mobile-review-research-finish"
          :disabled="loading || !answerIsValid"
        />
      </div>
    </div>
    <v-external-redirect-modal
      v-if="showExternalModal"
      :external-party-slug="this.externalPartySlug"
      :research-set-identifier="this.researchSetIdentifier"
      :user-identifier="this.userIdentifier"
      @rewards-claimed="completeSessionAndRedirect"
    />
    <div
      class="loader d-flex justify-content-center align-items-center"
      v-else-if="loading"
    >
      <v-loader/>
    </div>
  </div>
  <v-auth-modal
    v-else-if="showAuthModal"
    sub-title="sentence.login_to_submit_review"
    @authenticated="initReviewSessionAfterLogin"
  />
</template>

<script>
import {
  notify,
  pageNotFound,
} from '@/common/utils/notify';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import {
  completeResearchSession,
  deleteResearchReviewSession,
  fetchExternalParty,
  fetchResearch,
  fetchResearchItem,
  fetchResearchSetIdentifier,
  updateResearchAnswer,
} from '@/reviewer/api';
import Button from '@/common/components/Button.vue';
import DropdownMenu from '@/common/components/DropdownMenu.vue';
import ExternalRedirectModal from '@/reviewer/components/ExternalRedirectModal.vue';
import Loader from '@/common/components/Loader.vue';
import CripNotice from 'crip-vue-notice';
import AuthModal from '@/common/components/auth/AuthModal.vue';
import { ITEM_COMPONENTS } from '@/reviewer/constants';
import {
  formattedItemText,
  isMultiChoice,
} from '@/common/services/items';
import { numberOfOptionsValid } from '@/reviewer/services/reviewResearch';


export default {
  name: 'ReviewResearch',
  components: {
    'v-button': Button,
    'v-auth-modal': AuthModal,
    'v-dropdown-menu': DropdownMenu,
    'v-external-redirect-modal': ExternalRedirectModal,
    'v-loader': Loader,
  },
  computed: {
    ...mapGetters(['user']),
    hasCoins() {
      // TODO: coins_per_review should be 0 if the research does not have coins;
      return this.research.has_coins && this.research.coins_per_review > 0;
    },
    membershipSlugs() {
      if (this.user.isAuthenticated) {
        return this.user.memberships.map((membership) => membership.organisation.slug);
      }
      return [];
    },
    externalPartySlug() {
      return this.$route.query.ep;
    },
    userIdentifier() {
      return this.$route.query.uid;
    },
    researchSetIdentifier() {
      return this.$route.query.rsid;
    },
    total() {
      return this.research.max_path_length;
    },
    isTestReview() {
      return this.$route.query.test === 'true';
    },
    answerIsValid() {
      if (this.researchItem.render_type === 'no_field') {
        return true;
      }
      const hasValue = (
        this.researchItem.answer.value
        || this.researchItem.answer.value_alt
        || this.researchItem.answer.value?.length > 0
      );
      if (isMultiChoice(this.researchItem.render_type)) {
        return hasValue && numberOfOptionsValid(
          this.researchItem.item.config,
          this.researchItem.answer.value,
        );
      }
      return hasValue;
    },
  },
  data() {
    return {
      ITEM_COMPONENTS,
      research: null,
      session: null,
      researchItem: null,
      showAuthModal: false,
      initValue: null,
      loading: false,
      transitionName: null,
      isExternal: false,
      showExternalModal: false,
      progress: 0,
    };
  },
  methods: {
    ...mapActions(['refreshUser']),
    formattedItemText,
    async getResearchItem(itemId, sessionUUID) {
      this.initValue = null;
      const response = await fetchResearchItem(
        this.$route.params.uuid,
        itemId,
        sessionUUID,
      );
      if (response.status === 403) {
        // Reviewer exceeded the number of reviews for this hour;
        await this.$router.push({ name: 'max-reviews' });
        return;
      }

      this.researchItem = response.data;
      this.progress = (response.data.progress || 0) * 100;
      this.initValue = response.data.answer.value;
    },
    initReviewSessionAfterLogin() {
      notify('phrase.logged_in', 'sentence.start_reviewing');
      this.showAuthModal = false;
      this.initReviewSession();
    },
    sendToRemovedPage() {
      this.$router.push({ name: 'removed-from-research', params: { uuid: this.$route.params.uuid } });
    },
    async initReviewSession() {
      const response = await fetchResearch(this.$route.params.uuid, this.$route.query.test);
      if (response.status !== 200) {
        pageNotFound();
        await this.$router.push({ name: 'dashboard' });
        return;
      }
      this.research = response.data;
      if (this.membershipSlugs.includes(this.research.organisation.slug) && !this.isTestReview) {
        await this.$router.push({ name: 'own-commercial' });
        return;
      }

      this.session = this.research.session;
      if (!this.session) {
        notify('phrase.max_reviews', 'sentence.max_reviews_short');
        await this.$router.push({ name: 'dashboard' });
      } else if (this.session.reviewed) {
        notify('phrase.research_reviewed', 'sentence.research_reviewed');
        await this.$router.push({ name: 'dashboard' });
        return;
      } else if (this.session.removed) {
        this.sendToRemovedPage();
      }

      await this.getResearchItem(this.session.current_research_item, this.session.uuid);
    },
    async restartResearch() {
      await deleteResearchReviewSession(this.session.uuid);
      document.location.reload();
    },
    async redirectExternal() {
      const response = await fetchResearchSetIdentifier(
        this.externalPartySlug,
        this.researchSetIdentifier,
      );
      if (response.data.review_cycles_left > 1) {
        // Greater than one is because the user is in its current review session;
        const sessionCompleted = await this.completeSession();
        if (sessionCompleted) {
          // eslint-disable-next-line no-new
          new CripNotice({
            title: this.$t('phrase.answers_saved'),
            description: this.$t('sentence.answers_saved_cycles_left', {
              cyclesLeft: response.data.review_cycles_left - 1,
            }),
          });
          if (this.hasCoins) {
            await this.$store.commit('setCoinAccountChanged', true);
          }
          await this.$router.push({
            name: 'research-set-review',
            params: {
              uuid: response.data.research_set_uuid,
            },
            query: this.$route.query,
          });
        }
      } else {
        this.showExternalModal = true;
      }
    },
    async completeSessionAndRedirect(redirectUrl, formData) {
      const sessionCompleted = await this.completeSession(formData);
      if (sessionCompleted) {
        document.location.href = redirectUrl;
      } else {
        notify('phrase.error', 'sentence.error_occurred');
      }
    },
    async completeSession(formData) {
      const response = await completeResearchSession(
        this.session.uuid,
        {
          external_party: this.$route.query.ep,
          rsid: this.researchSetIdentifier,
          uid: this.userIdentifier,
          ctype: 'set',
          ...formData,
        },
      );
      await this.refreshUser();
      return response.status === 200;
    },
    async saveAndFinish() {
      this.loading = true;
      await this.saveAnswer();
      if (this.isExternal) {
        await this.redirectExternal();
        return;
      }
      const sessionCompleted = await this.completeSession();
      if (sessionCompleted) {
        notify('phrase.answers_saved', 'sentence.answers_saved_successfully');
        await this.$router.push({ name: 'dashboard' });
      }
      this.loading = false;
    },
    async saveAndLeave() {
      this.loading = true;
      await this.saveAnswer();
      await this.$router.push({ name: 'dashboard' });
    },
    async saveAndNext() {
      this.loading = true;
      const response = await this.saveAnswer();
      if (response.data.next_research_item) {
        if (response.data.next_research_item !== 'removed') {
          await this.getResearchItem(response.data.next_research_item, this.session.uuid);
          this.loading = false;
          this.transitionName = 'fade-right-nl';
        } else if (this.isExternal) {
          const epResponse = await fetchExternalParty(
            this.externalPartySlug,
            this.researchSetIdentifier,
            this.userIdentifier,
          );
          if (epResponse.data.screen_out_redirect_url) {
            document.location.href = epResponse.data.screen_out_redirect_url;
          } else {
            this.sendToRemovedPage();
          }
        } else {
          this.sendToRemovedPage();
        }
      } else {
        await this.saveAndFinish();
      }
    },
    async saveAndNextOrFinish() {
      if (this.researchItem.has_next) {
        await this.saveAndNext();
      }
    },
    async saveAndPrevious() {
      this.loading = true;
      if (this.researchItem.answer.value) {
        await this.saveAnswer();
      }
      await this.getResearchItem(this.researchItem.previous_research_item, this.session.uuid);
      this.loading = false;
      this.transitionName = 'fade-left-nl';
    },
    async saveAnswer() {
      const response = await updateResearchAnswer(
        this.researchItem.answer.id,
        {
          value: JSON.stringify(this.researchItem.answer.value),
          value_alt: this.researchItem.answer.value_alt,
        },
      );
      return response;
    },
  },
  created() {
    this.isExternal = !!this.externalPartySlug;

    if (!this.user.isAuthenticated) {
      this.showAuthModal = true;
    } else {
      this.initReviewSession();
    }
  },
};
</script>

<style scoped>
.top-bar {
  height: 67px;
}

.fixed-top-bar {
  position: fixed;
  top: 0;
  min-width: 50px;
  z-index: 1;
}


.bottom-bar {
  height: 67px;
  background: linear-gradient(0deg,
  rgba(255, 255, 255, 1) 0%, rgba(0, 0, 0, 0) 100%);
  position: fixed;
}

.research-item-container {
  position: fixed;
  width: 100%;
  top: 0;
  bottom: 0;
  padding: 67px 30px 67px 30px;
  overflow: auto;
}

.research-item-inner {
  margin-bottom: 50px;
  max-width: 900px;
}

.container-review {
  height: 100vh;
}

.label {
  font-size: 30px;
}

.btn-left {
  position: fixed;
  left: 0;
  bottom: 0;
}

.btn-right {
  position: fixed;
  right: 0;
  bottom: 0;
}

.loader {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
}

@media (min-width: 768px) {
  /* Medium and larger screens (small screen excluded) */
  .research-item-inner {
    min-width: 700px;
  }

  .research-item-container {
    padding-bottom: 1rem;
    padding-top: 1rem;
  }
}
</style>
